import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, Globe, Loader2, MapPin, Phone, Navigation, Star, Store } from "lucide-react";
import { api, publicAssetUrl } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

const humanize = (slug) =>
    (slug || "")
        .replace(/-/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Public /partner-shops/{id} detail page. Renders a hero card, an optional
 * "Visit Website" button, and the branches grouped by district. Fires
 * `post_view` analytics on load and on Call/Directions taps.
 */
export default function CustomerPartnerShopDetailPage() {
    const { id } = useParams();
    const [brand, setBrand] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        if (!id) return;
        setLoading(true);
        setNotFound(false);
        api.get(`/partner-shops/${id}`)
            .then((r) => setBrand(r.data))
            .catch((e) => {
                if (e?.response?.status === 404) setNotFound(true);
            })
            .finally(() => setLoading(false));
    }, [id]);

    // Fire the view event once per successful load.
    useEffect(() => {
        if (!brand?.slug) return;
        trackEvent("post_view", {
            post_id: `partner-shop:${brand.slug}`,
            target_type: "partner_shop",
            target_id: brand.slug,
        });
    }, [brand?.slug]);

    const grouped = useMemo(() => {
        const map = {};
        (brand?.branches || []).forEach((b, idx) => {
            const d = b.district_slug || "other";
            if (!map[d]) map[d] = [];
            map[d].push({ ...b, __idx: idx });
        });
        return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
    }, [brand?.branches]);

    if (loading) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-white">
                <Loader2 size={28} className="animate-spin text-[var(--brand-orange)]" />
            </div>
        );
    }

    if (notFound || !brand) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-white px-6 text-center">
                <Store size={40} className="text-[var(--brand-orange)]" />
                <div className="font-heading text-2xl font-extrabold text-[var(--text-primary)]">
                    Brand not found
                </div>
                <p className="max-w-md font-body text-sm text-[var(--text-muted)]">
                    The partner brand you're looking for doesn't exist or has been removed.
                </p>
                <Link to="/partner-shops" className="btn-primary rounded-full">
                    Back to Partner Shops
                </Link>
            </div>
        );
    }

    const branchCount = brand.branches?.length || 0;
    const initial = (brand.brand_name || "?").trim().charAt(0).toUpperCase();

    const onCall = (branch) => {
        trackEvent("post_view", {
            post_id: `partner-shop-call:${brand.slug}`,
            target_type: "partner_shop_branch_call",
            target_id: brand.slug,
        });
    };
    const onDirections = (branch) => {
        trackEvent("post_view", {
            post_id: `partner-shop-directions:${brand.slug}`,
            target_type: "partner_shop_directions",
            target_id: brand.slug,
        });
    };

    return (
        <div className="min-h-screen bg-white pb-24" data-testid="customer-partner-shop-detail-page">
            {/* Sticky header */}
            <header className="sticky top-0 z-10 flex items-center gap-3 border-b border-slate-100 bg-white/95 px-4 py-3 backdrop-blur-md md:px-8">
                <Link
                    to="/partner-shops"
                    className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#FFF3E6] text-[var(--brand-orange)] hover:opacity-80"
                    data-testid="partner-shop-detail-back-btn"
                    aria-label="Back to Partner Shops"
                >
                    <ArrowLeft size={18} />
                </Link>
                <div className="min-w-0 flex-1">
                    <h1
                        className="truncate font-heading text-lg font-extrabold text-[var(--text-primary)] md:text-xl"
                        data-testid="partner-shop-detail-name"
                    >
                        {brand.brand_name}
                    </h1>
                </div>
                {brand.is_featured && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[var(--brand-orange)] px-3 py-1 font-heading text-[10px] font-bold uppercase tracking-wider text-white">
                        <Star size={10} className="fill-white" /> Featured
                    </span>
                )}
            </header>

            {/* Hero */}
            <section
                className="mx-auto flex max-w-3xl flex-col items-center px-4 py-8 text-center md:py-10"
                data-testid="partner-shop-detail-hero"
            >
                <div className="mb-4 flex h-28 w-28 items-center justify-center overflow-hidden rounded-3xl bg-[#FFF3E6] ring-1 ring-orange-100 md:h-32 md:w-32">
                    {brand.logo_url ? (
                        <img
                            src={publicAssetUrl(brand.logo_url)}
                            alt={`${brand.brand_name} logo`}
                            className="h-24 w-24 object-contain md:h-28 md:w-28"
                        />
                    ) : (
                        <span className="font-heading text-5xl font-extrabold text-[var(--brand-orange)]">
                            {initial}
                        </span>
                    )}
                </div>
                <h2 className="font-heading text-2xl font-extrabold text-[var(--text-primary)] md:text-3xl">
                    {brand.brand_name}
                </h2>
                {brand.tagline && (
                    <p className="mt-2 max-w-md font-body text-sm text-[var(--text-muted)] md:text-base">
                        {brand.tagline}
                    </p>
                )}
                {brand.category_slug && (
                    <span className="mt-3 inline-flex items-center rounded-full bg-[#FFF3E6] px-3 py-1 font-heading text-xs font-semibold capitalize text-[var(--brand-orange)]">
                        {humanize(brand.category_slug)}
                    </span>
                )}

                {brand.website_url && (
                    <a
                        href={brand.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-primary mt-6 inline-flex items-center gap-2 rounded-full px-6 py-3"
                        data-testid="partner-shop-visit-website-btn"
                    >
                        <Globe size={16} />
                        Visit Website
                        <ExternalLink size={14} />
                    </a>
                )}
            </section>

            {/* Branches */}
            <section className="mx-auto max-w-3xl px-4 pb-10 md:px-6" data-testid="partner-shop-branches-section">
                <h3 className="mb-4 font-heading text-xl font-extrabold text-[var(--text-primary)]">
                    Branches ({branchCount})
                </h3>

                {branchCount === 0 ? (
                    <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center font-body text-sm text-[var(--text-muted)]">
                        No branches listed yet for this brand.
                    </div>
                ) : (
                    grouped.map(([district, distBranches]) => (
                        <div key={district} className="mb-6" data-testid={`partner-shop-district-group-${district}`}>
                            <div className="mb-3 flex items-center gap-2">
                                <MapPin size={16} className="text-[var(--brand-orange)]" />
                                <h4 className="font-heading text-base font-bold capitalize text-[var(--text-primary)] md:text-lg">
                                    {humanize(district)}
                                </h4>
                                <span className="font-body text-xs text-[var(--text-muted)]">
                                    · {distBranches.length} branch{distBranches.length === 1 ? "" : "es"}
                                </span>
                            </div>

                            <div className="space-y-3">
                                {distBranches.map((branch, i) => (
                                    <div
                                        key={`${district}-${i}`}
                                        className="card-soft flex flex-col gap-2"
                                        data-testid={`partner-shop-branch-${district}-${i}`}
                                    >
                                        <div className="font-heading text-sm font-semibold text-[var(--text-primary)]">
                                            {branch.address}
                                        </div>
                                        {(branch.hours || branch.contact_number) && (
                                            <div className="flex flex-wrap gap-x-4 gap-y-1 font-body text-xs text-[var(--text-muted)]">
                                                {branch.hours && (
                                                    <span>🕒 {branch.hours}</span>
                                                )}
                                                {branch.contact_number && (
                                                    <span>📞 {branch.contact_number}</span>
                                                )}
                                            </div>
                                        )}
                                        {(branch.contact_number || branch.google_maps_url) && (
                                            <div className="mt-2 flex flex-wrap gap-2">
                                                {branch.contact_number && (
                                                    <a
                                                        href={`tel:${branch.contact_number}`}
                                                        onClick={() => onCall(branch)}
                                                        data-testid={`partner-shop-call-${brand.slug}-${branch.__idx}`}
                                                        className="btn-primary inline-flex items-center gap-1 rounded-full px-4 py-2 !text-xs"
                                                    >
                                                        <Phone size={12} /> Call
                                                    </a>
                                                )}
                                                {branch.google_maps_url && (
                                                    <a
                                                        href={branch.google_maps_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        onClick={() => onDirections(branch)}
                                                        data-testid={`partner-shop-directions-${brand.slug}-${branch.__idx}`}
                                                        className="inline-flex items-center gap-1 rounded-full border border-[var(--brand-orange)] bg-white px-4 py-2 font-heading text-xs font-semibold text-[var(--brand-orange)] hover:bg-[#FFF3E6]"
                                                    >
                                                        <Navigation size={12} /> Directions
                                                    </a>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))
                )}
            </section>
        </div>
    );
}
